import React from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionHeading } from '../components/ui/SectionHeading';
import { StepList } from '../components/StepList';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { EntrepriseForm } from '../components/EntrepriseForm';
import { ContactCTA } from '../components/ContactCTA';
import { useSeo } from '../hooks/useSeo';
import { Users, FileText, Target, CheckCircle, FileSignature, HeartHandshake, Search, Briefcase, Euro, Award, ArrowDownCircle, Sparkles, Info } from 'lucide-react';
const NTC_BLOC_1 = ['Assurer une veille commerciale sur le marché, la concurrence et les opportunités', 'Identifier et segmenter les entreprises ciblées', 'Définir des cibles prioritaires et actualiser les profils clients/prospects', "Construire et mettre en œuvre un plan d'actions commerciales", 'Prospecter par téléphone, e-mail, réseau, terrain ou outils digitaux', 'Utiliser les outils numériques et le CRM pour organiser la prospection et suivre l\'activité', "Analyser les résultats obtenus à l'aide d'indicateurs de performance", 'Proposer et mettre en œuvre des actions correctives'];
const NTC_BLOC_2 = ["Représenter l'entreprise et valoriser son image", 'Analyser les besoins, attentes et contraintes du client', 'Concevoir une proposition technique et commerciale personnalisée', 'Vérifier la faisabilité de la proposition et sa rentabilité commerciale', 'Intégrer les enjeux de transition écologique dans la solution proposée', "Préparer l'argumentaire et traiter les objections", 'Présenter, argumenter et négocier la solution', 'Assurer le suivi de la solution mise en œuvre', 'Réaliser le bilan, ajuster son activité commerciale et rendre compte à la hiérarchie', 'Consolider la relation client et détecter de nouvelles opportunités commerciales'];
const ENTREPRISE_STEPS = [{
  title: 'Sourcing et définition du besoin',
  description: 'Analyse de votre poste et définition du profil idéal avec nos conseillers.',
  icon: <Search className="w-6 h-6" data-id="element-1433" data-mp="ae14olq83wiv" />
}, {
  title: 'Présélection des candidats',
  description: 'Nous vous présentons uniquement des profils qualifiés, motivés et préparés.',
  icon: <CheckCircle className="w-6 h-6" data-id="element-1434" data-mp="1dbb7oofqi8b" />
}, {
  title: 'Aide aux démarches administratives',
  description: "Accompagnement dans la rédaction et la signature du contrat d'apprentissage (CERFA).",
  icon: <FileSignature className="w-6 h-6" data-id="element-1435" data-mp="u29wgunuu0hu" />
}, {
  title: 'Suivi personnalisé',
  description: "Points réguliers avec le tuteur en entreprise tout au long du contrat pour garantir le succès de l'alternance.",
  icon: <HeartHandshake className="w-6 h-6" data-id="element-1436" data-mp="1hq05sve6ss2c" />
}];
export function Entreprises() {
  useSeo({
    title: "Recruter un alternant dans l'Yonne — Aides jusqu'à 6 000 €",
    description: "Recrutez un apprenti en commerce ou relation client dans l'Yonne. Sourcing et présélection gratuits, aides de l'État jusqu'à 6 000 €, prise en charge OPCO et accompagnement administratif par notre CFA.",
    path: '/entreprises'
  });
  const scrollToForm = () => {
    document.getElementById('formulaire-recrutement')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="flex flex-col w-full" data-id="element-1437" data-mp="238sauhybi9mt">
      <PageHeader title="Espace Entreprises" subtitle="Recrutez vos futurs collaborateurs et dynamisez vos équipes grâce à l'alternance avec Zillenium." data-id="element-1438" data-mp="237qupz2g2wy8" />

      {/* Top Jump Button */}
      <div className="bg-primary-dark text-white pb-12 pt-4 text-center -mt-1" data-id="element-1439" data-mp="2czwn39wlzgdy">
        <Button onClick={scrollToForm} className="bg-brand-orange hover:bg-[#E67E22] text-white text-lg px-8 py-6 rounded-full shadow-lg hover:scale-105 transition-transform" data-id="element-1440" data-mp="2hxya25x5nzup">
          Confier un recrutement <ArrowDownCircle className="ml-2 w-5 h-5" data-id="element-1441" data-mp="2qywfmsn8sffe" />
        </Button>
      </div>

      {/* Pourquoi recruter */}
      <section className="py-24 bg-white relative overflow-hidden" data-id="element-1442" data-mp="2d9jgmylic46b">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-lavande rounded-full blur-[100px] opacity-30 pointer-events-none" data-id="element-1443" data-mp="2999exy4qzzx9" />
        <div className="container max-w-6xl mx-auto relative z-10" data-id="element-1444" data-mp="2n3a4c19t8aky">
          <SectionHeading title="Pourquoi recruter avec Zillenium Alternance ?" centered data-id="element-1445" data-mp="2i2w6td5u4j16" />
          <div className="grid md:grid-cols-3 gap-8 mt-16" data-id="element-1446" data-mp="1ueutlqxpoeff">
            <Card className="border-none shadow-xl hover:-translate-y-2 transition-all duration-300 bg-white group" data-id="element-1447" data-mp="1qchxzvcohwwd">
              <div className="h-2 w-full bg-brand-orange" data-id="element-1448" data-mp="24ciuyzptok6k" />
              <CardContent className="p-8 text-center" data-id="element-1449" data-mp="1zblhv1jo6gbx">
                <div className="w-20 h-20 bg-lavande rounded-3xl flex items-center justify-center mx-auto mb-8 group-hover:scale-110 transition-transform duration-300" data-id="element-1450" data-mp="1zdxtyy2qxwh2">
                  <Target className="w-10 h-10 text-brand-orange animate-soft-bounce" data-id="element-1451" data-mp="3aq8jmvm71cha" />
                </div>
                <h3 className="font-extrabold font-heading text-2xl text-primary-dark mb-4" data-id="element-1452" data-mp="whpj6w8aa21n">
                  Des profils ciblés
                </h3>
                <p className="text-lg text-muted-foreground leading-relaxed" data-id="element-1453" data-mp="nj41f3amyr1x">
                  Des candidats préparés aux réalités du terrain et formés aux
                  techniques commerciales actuelles.
                </p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-xl hover:-translate-y-2 transition-all duration-300 bg-white group" data-id="element-1454" data-mp="1g9m3q23jcoea">
              <div className="h-2 w-full bg-primary" data-id="element-1455" data-mp="16homoepuj27n" />
              <CardContent className="p-8 text-center" data-id="element-1456" data-mp="16h6kl1gi5trt">
                <div className="w-20 h-20 bg-primary/10 rounded-3xl flex items-center justify-center mx-auto mb-8 group-hover:scale-110 transition-transform duration-300" data-id="element-1457" data-mp="1bfq7b42x79l8">
                  <FileText className="w-10 h-10 text-primary animate-soft-float" data-id="element-1458" data-mp="23wu88ht53tf9" />
                </div>
                <h3 className="font-extrabold font-heading text-2xl text-primary-dark mb-4" data-id="element-1459" data-mp="2dsj836mw1afg">
                  Zéro tracas administratif
                </h3>
                <p className="text-lg text-muted-foreground leading-relaxed" data-id="element-1460" data-mp="28u9fn3sjsnxj">
                  Nous vous guidons dans toutes les étapes de contractualisation
                  de votre alternant.
                </p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-xl hover:-translate-y-2 transition-all duration-300 bg-white group" data-id="element-1461" data-mp="1uu6wzfcf1qh8">
              <div className="h-2 w-full bg-brand-orange" data-id="element-1462" data-mp="1q2gicx6g4gk0" />
              <CardContent className="p-8 text-center" data-id="element-1463" data-mp="1l3vt1ssqfhac">
                <div className="w-20 h-20 bg-lavande rounded-3xl flex items-center justify-center mx-auto mb-8 group-hover:scale-110 transition-transform duration-300" data-id="element-1464" data-mp="1l4f0gfkrana4">
                  <Users className="w-10 h-10 text-brand-orange animate-soft-pulse" data-id="element-1465" data-mp="1b4wi23khv9k6" />
                </div>
                <h3 className="font-extrabold font-heading text-2xl text-primary-dark mb-4" data-id="element-1466" data-mp="175ffbhwgcqip">
                  Un partenaire de proximité
                </h3>
                <p className="text-lg text-muted-foreground leading-relaxed" data-id="element-1467" data-mp="3mtb77mxsdq6s">
                  Un interlocuteur unique et réactif pour assurer le suivi de
                  votre apprenti en entreprise.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Missions en entreprise */}
      <section className="py-24 bg-muted" data-id="element-1468" data-mp="2z5bfajoqntqm">
        <div className="container max-w-6xl mx-auto" data-id="element-1469" data-mp="342tfnloox0n0">
          <SectionHeading title="Les missions en entreprise" subtitle="Quelles responsabilités confier à nos alternants pour les faire monter en compétences ?" centered data-id="element-1470" data-mp="343cx71afhn0i" />
          <div className="grid md:grid-cols-2 gap-8 mt-16" data-id="element-1471" data-mp="3dtxwkqxxevkh">
            <Card className="border-none shadow-lg bg-white hover:shadow-xl transition-shadow" data-id="element-1472" data-mp="2gc7iwqk4z2sb">
              <CardContent className="p-8" data-id="element-1473" data-mp="2lbk7pgukijxj">
                <div className="flex items-center gap-4 mb-6" data-id="element-1474" data-mp="2la8yv2m9mlzk">
                  <div className="w-12 h-12 bg-lavande rounded-xl flex items-center justify-center" data-id="element-1475" data-mp="1epstyr1jidyu">
                    <Briefcase className="w-6 h-6 text-primary-dark" data-id="element-1476" data-mp="19r83vkg05ao4" />
                  </div>
                  <h3 className="font-extrabold font-heading text-2xl text-primary-dark" data-id="element-1477" data-mp="1nqr3fouqzdot">
                    Missions BTS NDRC
                  </h3>
                </div>
                <ul className="space-y-4 text-slate-600" data-id="element-1478" data-mp="1xnc5q2fduaoy">
                  <li className="flex items-start gap-3" data-id="element-1479" data-mp="1xlrasbp5opqk">
                    <CheckCircle className="w-5 h-5 text-brand-orange shrink-0 mt-0.5" data-id="element-1480" data-mp="1snpt9v7doyh7" />
                    <span data-id="element-1481" data-mp="26gfg21uqe7fd">
                      <strong data-id="element-1482" data-mp="26fnvdm2o9u3v">Prospection et développement :</strong> Recherche
                      de nouveaux clients, qualification de fichiers, prise de
                      rendez-vous.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-1483" data-mp="2bfa9dxqbredq">
                    <CheckCircle className="w-5 h-5 text-brand-orange shrink-0 mt-0.5" data-id="element-1484" data-mp="2bgtovm45pleg" />
                    <span data-id="element-1485" data-mp="2v2dgxhm6h1i5">
                      <strong data-id="element-1486" data-mp="2uyzd04xv8k8l">Négociation et vente :</strong> Conduite
                      d'entretiens commerciaux, élaboration de propositions,
                      conclusion de ventes.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-1487" data-mp="2q1fz5652y9h6">
                    <CheckCircle className="w-5 h-5 text-brand-orange shrink-0 mt-0.5" data-id="element-1488" data-mp="2q425x3k5ckyc" />
                    <span data-id="element-1489" data-mp="2c99izk1xg2hq">
                      <strong data-id="element-1490" data-mp="2h1arcuvz592h">Relation client digitale :</strong> Animation de
                      réseaux sociaux, gestion de l'e-réputation, e-mailing.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-1491" data-mp="27bhhpzfe740v">
                    <CheckCircle className="w-5 h-5 text-brand-orange shrink-0 mt-0.5" data-id="element-1492" data-mp="27bzjtcoqkcgp" />
                    <span data-id="element-1493" data-mp="1tczv65183xkj">
                      <strong data-id="element-1494" data-mp="1tdt6zkf1hd9j">Fidélisation :</strong> Suivi client, gestion des
                      réclamations, ventes additionnelles.
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg bg-white hover:shadow-xl transition-shadow" data-id="element-1495" data-mp="yy5prqh4n2bc">
              <CardContent className="p-8" data-id="element-1496" data-mp="kywdh1w1kscn">
                <div className="flex items-center gap-4 mb-6" data-id="element-1497" data-mp="l07faw5xfq14">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center" data-id="element-1498" data-mp="p0r9prz48fjk">
                    <Briefcase className="w-6 h-6 text-primary" data-id="element-1499" data-mp="1cqlmyanck64c" />
                  </div>
                  <h3 className="font-extrabold font-heading text-2xl text-primary-dark" data-id="element-1500" data-mp="1hnc1uunf0vo6">
                    Missions TP Conseiller de Vente
                  </h3>
                </div>
                <ul className="space-y-4 text-slate-600" data-id="element-1501" data-mp="17y2d9ors5v4t">
                  <li className="flex items-start gap-3" data-id="element-1502" data-mp="17whiby1k0a6f">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" data-id="element-1503" data-mp="1vn3kj8ovn08i" />
                    <span data-id="element-1504" data-mp="20f4v8q6eciwf">
                      <strong data-id="element-1505" data-mp="20gfzss7o1uol">Accueil et conseil :</strong> Prise en charge du
                      client, découverte des besoins, argumentation adaptée.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-1506" data-mp="3g5nv8tb2f5i0">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" data-id="element-1507" data-mp="3uvmrurmz28vd" />
                    <span data-id="element-1508" data-mp="3pwa6kbbr19uw">
                      <strong data-id="element-1509" data-mp="3px1r8r3t5n6e">Vente et encaissement :</strong> Concrétisation de
                      la vente, proposition de services annexes, gestion de la
                      caisse.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-1510" data-mp="2x960e30x6phj">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" data-id="element-1511" data-mp="3c4vkfvmjosjm" />
                    <span data-id="element-1512" data-mp="374zjy6d23j8t">
                      <strong data-id="element-1513" data-mp="374ps0d8gb20h">Merchandising :</strong> Mise en rayon, maintien
                      de l'attractivité du point de vente, gestion des vitrines.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-1514" data-mp="merzw77pq8w2">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" data-id="element-1515" data-mp="tizuj3rmjhnq" />
                    <span data-id="element-1516" data-mp="yhktz0z1xxb4">
                      <strong data-id="element-1517" data-mp="ygrw6pi2mi54">Gestion des stocks :</strong> Réception des
                      marchandises, inventaires, suivi des ruptures.
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Missions TP Négociateur Technico-Commercial */}
            <Card className="md:col-span-2 border-none shadow-2xl ring-2 ring-brand-orange bg-white overflow-hidden" data-id="element-1518" data-mp="atbd8dgp3zqu">
              <div className="h-2 w-full bg-brand-orange" data-id="element-1519" data-mp="frcwaye4fz2b" />
              <CardContent className="p-8 md:p-10" data-id="element-1520" data-mp="kpwimzwmncv7">
                <div className="flex flex-wrap items-center gap-4 mb-3" data-id="element-1521" data-mp="kobq1ftvi3zz">
                  <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center" data-id="element-1522" data-mp="pifw7wvbe800">
                    <Briefcase className="w-6 h-6 text-brand-orange" data-id="element-1523" data-mp="3o6mxm4a6t091" />
                  </div>
                  <h3 className="font-extrabold font-heading text-2xl text-primary-dark" data-id="element-1524" data-mp="3t457bww13ji3">
                    Missions TP Négociateur Technico-Commercial
                  </h3>
                  <span className="inline-flex items-center gap-1.5 bg-brand-orange text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full" data-id="element-1525" data-mp="26abbu9cbi261">
                    <Sparkles className="w-3.5 h-3.5" data-id="element-1526" data-mp="1wbcdbjl9nmwr" />
                    Nouveauté
                  </span>
                </div>
                <p className="text-slate-600 leading-relaxed mb-8" data-id="element-1527" data-mp="1rd12p6n5d8dh">
                  Missions pouvant être confiées à l'apprenant en entreprise, en
                  cohérence avec les compétences visées par le titre
                  professionnel (RNCP39063).
                </p>

                <div className="grid md:grid-cols-2 gap-8" data-id="element-1528" data-mp="1dldwof3eklhi">
                  <div data-id="element-1529" data-mp="1dirqojw5ue1e">
                    <div className="flex items-center gap-3 bg-lavande rounded-xl px-4 py-3 mb-5" data-id="element-1530" data-mp="18m1a7vgg1gfg">
                      <Target className="w-5 h-5 text-primary shrink-0" data-id="element-1531" data-mp="13lwsy37poho6" />
                      <div data-id="element-1532" data-mp="13orejy2go8j6">
                        <span className="block text-xs font-bold uppercase tracking-wider text-primary" data-id="element-1533" data-mp="33o4rs0pabsf9">
                          Bloc 1
                        </span>
                        <span className="font-bold text-primary-dark leading-snug" data-id="element-1534" data-mp="38plodk0qksq1">
                          Élaborer une stratégie de prospection et la mettre en
                          œuvre
                        </span>
                      </div>
                    </div>
                    <ul className="space-y-3 text-slate-600" data-id="element-1535" data-mp="2kwvf5ik7wnjg">
                      {NTC_BLOC_1.map(mission => <li key={mission} className="flex items-start gap-3" data-id="element-1536" data-mp="2puwze6tdqswi">
                          <CheckCircle className="w-5 h-5 text-brand-orange shrink-0 mt-0.5" data-id="element-1537" data-mp="2b1tp8ki4bhft" />
                          <span data-id="element-1538" data-mp="2fzc2gn364itm">{mission}</span>
                        </li>)}
                    </ul>
                  </div>

                  <div data-id="element-1539" data-mp="e88phr5t2s5w">
                    <div className="flex items-center gap-3 bg-lavande rounded-xl px-4 py-3 mb-5" data-id="element-1540" data-mp="e6nvc2nelb8k">
                      <HeartHandshake className="w-5 h-5 text-primary shrink-0" data-id="element-1541" data-mp="1yqswp1sscfan" />
                      <div data-id="element-1542" data-mp="1trg19snulzwg">
                        <span className="block text-xs font-bold uppercase tracking-wider text-primary" data-id="element-1543" data-mp="1ts9cp6xr5dkx">
                          Bloc 2
                        </span>
                        <span className="font-bold text-primary-dark leading-snug" data-id="element-1544" data-mp="23okq6092w9gy">
                          Négocier une solution technique et commerciale et
                          consolider l'expérience client
                        </span>
                      </div>
                    </div>
                    <ul className="space-y-3 text-slate-600" data-id="element-1545" data-mp="2reot05eg056w">
                      {NTC_BLOC_2.map(mission => <li key={mission} className="flex items-start gap-3" data-id="element-1546" data-mp="2mgnb3nsr6bx0">
                          <CheckCircle className="w-5 h-5 text-brand-orange shrink-0 mt-0.5" data-id="element-1547" data-mp="wzm86l0c9bab" />
                          <span data-id="element-1548" data-mp="s1uf3nnyjj4j">{mission}</span>
                        </li>)}
                    </ul>
                  </div>
                </div>

                <div className="mt-8 flex items-start gap-3 bg-lavande/50 rounded-2xl p-5" data-id="element-1549" data-mp="2s5x0t50d4w2c">
                  <Info className="w-5 h-5 text-primary shrink-0 mt-0.5" data-id="element-1550" data-mp="2x4i277rcplsd" />
                  <p className="text-sm text-primary-dark leading-relaxed" data-id="element-1551" data-mp="312p97aw0j8qd">
                    Exemples de missions à adapter selon l'entreprise d'accueil
                    et le poste occupé.{' '}
                    <strong data-id="element-1552" data-mp="3b26alg48f0gj">Conforme au référentiel RNCP39063.</strong>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="mt-12 text-center" data-id="element-1553" data-mp="3ktaqqj5n8rcy">
            <Button onClick={scrollToForm} variant="outline" className="border-brand-orange text-brand-orange hover:bg-brand-orange hover:text-white text-lg px-8 py-6 rounded-full" data-id="element-1554" data-mp="3kultcfncrt2h">
              Proposer une mission <ArrowDownCircle className="ml-2 w-5 h-5" data-id="element-1555" data-mp="2neh8446y1xk" />
            </Button>
          </div>
        </div>
      </section>

      {/* Aides et Salaires (Vendre du rêve) */}
      <section className="py-24 bg-white relative overflow-hidden" data-id="element-1556" data-mp="7qxeijsr0mzc">
        <div className="container max-w-6xl mx-auto" data-id="element-1557" data-mp="7ttcu98iddob">
          <SectionHeading title="Un recrutement très avantageux" subtitle="Profitez d'aides exceptionnelles de l'État pour l'embauche de nos alternants." centered data-id="element-1558" data-mp="2t5cuy2axmhy" />

          <div className="grid md:grid-cols-3 gap-6 mt-16" data-id="element-1559" data-mp="vgjeh9gy368e">
            <Card className="bg-gradient-to-br from-primary to-primary-dark text-white border-none shadow-xl transform hover:-translate-y-2 transition-transform" data-id="element-1560" data-mp="qhg9fp0yt00f">
              <CardContent className="p-8 text-center" data-id="element-1561" data-mp="lovgeppvuqxy">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1562" data-mp="hol8h7ij5qgg">
                  <Euro className="w-8 h-8 text-white" data-id="element-1563" data-mp="1e93ptd0losog" />
                </div>
                <h3 className="text-4xl font-extrabold font-heading mb-2" data-id="element-1564" data-mp="19freu5ougke6">
                  5 000 €*
                </h3>
                <p className="text-lg font-medium text-lavande mb-4" data-id="element-1565" data-mp="1t39szdumtewg">par an</p>
                <p className="text-white/90" data-id="element-1566" data-mp="1xwmgtlrhlho5">
                  Aide pour l'embauche d'un{' '}
                  <strong data-id="element-1567" data-mp="1j7q0z2j6v0cz">TP Conseiller de Vente</strong> (Niveau 4)
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-brand-orange to-[#d6751f] text-white border-none shadow-xl transform hover:-translate-y-2 transition-transform" data-id="element-1568" data-mp="1o61fhqkfhz1j">
              <CardContent className="p-8 text-center" data-id="element-1569" data-mp="2gte03v713ws7">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1570" data-mp="21x6eqrjvwpbc">
                  <Euro className="w-8 h-8 text-white" data-id="element-1571" data-mp="26vq3ezpv4f7e" />
                </div>
                <h3 className="text-4xl font-extrabold font-heading mb-2" data-id="element-1572" data-mp="qhzloq5tlsfa">
                  4 500 €*
                </h3>
                <p className="text-lg font-medium text-white/80 mb-4" data-id="element-1573" data-mp="vhm0h41argq7">par an</p>
                <p className="text-white/90" data-id="element-1574" data-mp="39ov8ouv1cd74">
                  Aide pour l'embauche d'un <strong data-id="element-1575" data-mp="39n0rnrmvx58b">BTS NDRC</strong> ou d'un{' '}
                  <strong data-id="element-1576" data-mp="34qk00ngh88qh">TP Négociateur Technico-Commercial</strong> (Niveau 5)
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-lavande to-[#d4c1f9] text-primary-dark border-none shadow-xl transform hover:-translate-y-2 transition-transform" data-id="element-1577" data-mp="3infn3jtkc0pj">
              <CardContent className="p-8 text-center" data-id="element-1578" data-mp="2qz5hsiue8bkh">
                <div className="w-16 h-16 bg-primary-dark/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1579" data-mp="2lz99w8ikrzzn">
                  <Euro className="w-8 h-8 text-primary-dark" data-id="element-1580" data-mp="2zqxn6f4k8ui7" />
                </div>
                <h3 className="text-4xl font-extrabold font-heading mb-2" data-id="element-1581" data-mp="2v07fgv8nwfkc">
                  6 000 €*
                </h3>
                <p className="text-lg font-medium text-primary-dark/70 mb-4" data-id="element-1582" data-mp="279bjdlx6ay7a">
                  par an
                </p>
                <p className="text-primary-dark/90" data-id="element-1583" data-mp="2389rxr9hzqfv">
                  Aide maximale pour l'embauche d'un apprenti{' '}
                  <strong data-id="element-1584" data-mp="x8iwfk5tv1i8">PSH</strong> (Handicap)
                </p>
              </CardContent>
            </Card>
          </div>

          <p className="text-sm text-muted-foreground mt-6 text-center max-w-3xl mx-auto" data-id="element-1585" data-mp="1705arpdgpgp7">
            * Montants applicables aux entreprises de moins de 250 salariés.
            Pour les entreprises de 250 salariés et plus, l'aide est recalculée
            selon le niveau de qualification préparé :{' '}
            <button type="button" onClick={scrollToForm} className="text-brand-orange font-semibold underline underline-offset-2 hover:text-[#E67E22] transition-colors" data-id="element-1586" data-mp="1c01fjqtzhc5t">
              contactez-nous pour en savoir plus
            </button>
            .
          </p>

          <div className="mt-16" data-id="element-1587" data-mp="3rrffhepjbx9f">
            <ContactCTA title="Calculons ensemble le coût réel de votre recrutement" subtitle="Aides, niveau de prise en charge OPCO, reste à charge : un appel de 10 minutes suffit pour y voir clair. Aucun engagement." action={{
            label: 'Ou déposez votre offre en ligne',
            to: '/entreprises#formulaire-recrutement'
          }} data-id="element-1588" data-mp="3rsqlzmad7j48" />
          </div>

          {/* Table des salaires */}
          <div className="mt-20" data-id="element-1589" data-mp="1xh8xu9y1kf2z">
            <h3 className="text-2xl font-extrabold font-heading text-primary-dark mb-8 text-center" data-id="element-1590" data-mp="1xgfr38z31pku">
              Grille des salaires des alternants
            </h3>
            <div className="overflow-x-auto rounded-2xl shadow-xl border border-slate-100" data-id="element-1591" data-mp="1ojov0wkj6aq9">
              <table className="w-full text-left border-collapse bg-white min-w-[800px]" data-id="element-1592" data-mp="1ajxaeczidw3a">
                <thead data-id="element-1593" data-mp="1ekpjn68ds0zk">
                  <tr className="bg-primary/5 text-primary-dark" data-id="element-1594" data-mp="1ek7okcxgfst8">
                    <th className="p-4 font-bold border-b border-slate-200" data-id="element-1595" data-mp="34xf28c6y5f44">
                      Âge de l'alternant
                    </th>
                    <th className="p-4 font-bold border-b border-slate-200" data-id="element-1596" data-mp="38vmil5xi0ees">
                      1ère année
                    </th>
                    <th className="p-4 font-bold border-b border-slate-200" data-id="element-1597" data-mp="gpx0jfalpbbi">
                      2ème année
                    </th>
                    <th className="p-4 font-bold border-b border-slate-200" data-id="element-1598" data-mp="cpd46dxuqbqf">
                      3ème année
                    </th>
                  </tr>
                </thead>
                <tbody className="text-slate-600" data-id="element-1599" data-mp="14ey16gd6zi68">
                  <tr className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-id="element-1600" data-mp="14htzw6wv6avq">
                    <td className="p-4 font-medium text-primary-dark" data-id="element-1601" data-mp="10gs09oz1bwt8">
                      Moins de 18 ans
                    </td>
                    <td className="p-4" data-id="element-1602" data-mp="qpe5fui13l6f">
                      27% du Smic
                      <br data-id="element-1603" data-mp="1o404e3kq8zto" />
                      <span className="font-bold text-primary text-lg" data-id="element-1604" data-mp="1o48fbpxahv28">
                        504,10 €
                      </span>
                    </td>
                    <td className="p-4" data-id="element-1605" data-mp="1jbfaz35qgoqo">
                      39% du Smic
                      <br data-id="element-1606" data-mp="1qq983n0apxs5" />
                      <span className="font-bold text-primary text-lg" data-id="element-1607" data-mp="1vnhr1pt9q5ug">
                        728,14 €
                      </span>
                    </td>
                    <td className="p-4" data-id="element-1608" data-mp="20l14ini56elc">
                      55% du Smic
                      <br data-id="element-1609" data-mp="24ofvuabj6he3" />
                      <span className="font-bold text-primary text-lg" data-id="element-1610" data-mp="24pr0eccsvt69">
                        1 026,86 €
                      </span>
                    </td>
                  </tr>
                  <tr className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-id="element-1611" data-mp="2elbza1mkodbs">
                    <td className="p-4 font-medium text-primary-dark" data-id="element-1612" data-mp="2je54eqlydnoe">
                      18 à 20 ans
                    </td>
                    <td className="p-4" data-id="element-1613" data-mp="2obdowxukq3st">
                      43% du Smic
                      <br data-id="element-1614" data-mp="2sf35b013hf2r" />
                      <span className="font-bold text-primary text-lg" data-id="element-1615" data-mp="2sbp620nq9lzj">
                        802,82 €
                      </span>
                    </td>
                    <td className="p-4" data-id="element-1616" data-mp="2jp306aonfb5r">
                      51% du Smic
                      <br data-id="element-1617" data-mp="2fp2lqq1juzyp" />
                      <span className="font-bold text-primary text-lg" data-id="element-1618" data-mp="2fnrhyq83ts7l">
                        952,18 €
                      </span>
                    </td>
                    <td className="p-4" data-id="element-1619" data-mp="2oguv0xeu831p">
                      67% du Smic
                      <br data-id="element-1620" data-mp="2oixpbtn0wic8" />
                      <span className="font-bold text-primary text-lg" data-id="element-1621" data-mp="20r0eo5vb7wcp">
                        1 250,90 €
                      </span>
                    </td>
                  </tr>
                  <tr className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-id="element-1622" data-mp="1ws1bapp887nd">
                    <td className="p-4 font-medium text-primary-dark" data-id="element-1623" data-mp="2an3j5idmw72t">
                      21 à 25 ans*
                    </td>
                    <td className="p-4" data-id="element-1624" data-mp="3m43g4raxfa36">
                      53% du Smic
                      <br data-id="element-1625" data-mp="3h5ihgwbboygu" />
                      <span className="font-bold text-primary text-lg" data-id="element-1626" data-mp="3h6beh5khc9ls">
                        989,52 €
                      </span>
                    </td>
                    <td className="p-4" data-id="element-1627" data-mp="1303rgc1h1g0b">
                      61% du Smic
                      <br data-id="element-1628" data-mp="17y5bp0amvldd" />
                      <span className="font-bold text-primary text-lg" data-id="element-1629" data-mp="tx2mieocduy8">
                        1 138,88 €
                      </span>
                    </td>
                    <td className="p-4" data-id="element-1630" data-mp="ywpbtmhg31ne">
                      78% du Smic
                      <br data-id="element-1631" data-mp="1mli2p4j7mjb5" />
                      <span className="font-bold text-primary text-lg" data-id="element-1632" data-mp="1mm9vy8pc44y9">
                        1 456,28 €
                      </span>
                    </td>
                  </tr>
                  <tr className="hover:bg-slate-50 transition-colors" data-id="element-1633" data-mp="1cv5hrb7hgo4h">
                    <td className="p-4 font-medium text-primary-dark" data-id="element-1634" data-mp="1hui29pavtj3w">
                      26 ans et plus*
                    </td>
                    <td className="p-4" data-id="element-1635" data-mp="25jud0tluxz8b">
                      100% du Smic
                      <br data-id="element-1636" data-mp="2abkmkxcz0iyo" />
                      <span className="font-bold text-primary text-lg" data-id="element-1637" data-mp="2acvqcx6f1qps">
                        1 867,02 €
                      </span>
                    </td>
                    <td className="p-4" data-id="element-1638" data-mp="2f7otbse8clu2">
                      100% du Smic
                      <br data-id="element-1639" data-mp="2a951xcgvahub" />
                      <span className="font-bold text-primary text-lg" data-id="element-1640" data-mp="2a8c6h7nczerh">
                        1 867,02 €
                      </span>
                    </td>
                    <td className="p-4" data-id="element-1641" data-mp="25cen0sgstsah">
                      100% du Smic
                      <br data-id="element-1642" data-mp="219rragh0bn80" />
                      <span className="font-bold text-primary text-lg" data-id="element-1643" data-mp="1whqkha2lyopd">
                        1 867,02 €
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-sm text-muted-foreground mt-4 text-center" data-id="element-1644" data-mp="1mklu0y1qc72r">
              * Ou salaire minimum conventionnel (SMC) s'il est plus favorable.
              Les montants sont donnés à titre indicatif sur la base du SMIC en
              vigueur.
            </p>
          </div>

          <div className="mt-16 text-center" data-id="element-1645" data-mp="3p6zkdg453tll">
            <Button onClick={scrollToForm} className="bg-primary hover:bg-primary-dark text-white text-lg px-8 py-6 rounded-full shadow-lg hover:scale-105 transition-transform" data-id="element-1646" data-mp="3p8amzclumvb4">
              Estimer mon besoin de recrutement{' '}
              <ArrowDownCircle className="ml-2 w-5 h-5" data-id="element-1647" data-mp="6za29g8ik9vk" />
            </Button>
          </div>
        </div>
      </section>

      {/* Profils formés & Accompagnement */}
      <section className="py-24 bg-muted" data-id="element-1648" data-mp="3agsctvfo7uzo">
        <div className="container max-w-6xl mx-auto" data-id="element-1649" data-mp="3ahlbe94h7e6q">
          <div className="max-w-3xl mx-auto" data-id="element-1650" data-mp="3ffwnkqi8u0s4">
            {/* Accompagnement */}
            <div data-id="element-1651" data-mp="3k6v33nvequub">
              <SectionHeading title="Notre accompagnement" className="mb-12" data-id="element-1652" data-mp="3k7em77wsnp9x" />
              <StepList steps={ENTREPRISE_STEPS} data-id="element-1653" data-mp="2ms0q3jkuo4ua" />

              {/* Engagement Maître d'Apprentissage */}
              <div className="mt-12 bg-lavande/20 p-8 rounded-3xl border border-lavande/50 flex gap-6 items-start group hover:-translate-y-1 transition-transform" data-id="element-1654" data-mp="2rqamfxib9bhh">
                <div className="w-16 h-16 bg-lavande rounded-2xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform" data-id="element-1655" data-mp="1l5m49sxjvw51">
                  <Award className="w-8 h-8 text-primary-dark animate-soft-bounce" data-id="element-1656" data-mp="1u594wciggxx1" />
                </div>
                <div data-id="element-1657" data-mp="1p41us6submn7">
                  <h4 className="font-extrabold font-heading text-xl text-primary-dark mb-3" data-id="element-1658" data-mp="1p3s4sj7spfhi">
                    L'engagement Maître d'Apprentissage
                  </h4>
                  <p className="text-primary-dark/80 leading-relaxed font-medium" data-id="element-1659" data-mp="1z27zew6176vr">
                    En recrutant un alternant, vous vous engagez à désigner un
                    maître d'apprentissage chargé de l'accompagner, de lui
                    transmettre son savoir-faire et de suivre sa progression au
                    quotidien. Tout au long de la formation, des points
                    réguliers sont organisés entre l'entreprise, l'alternant et
                    notre équipe pédagogique. Chez Zillenium, on vous accompagne
                    pour de vrai.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA / Formulaire */}
      <section id="formulaire-recrutement" className="py-24 bg-primary-dark text-white relative overflow-hidden scroll-mt-16" data-id="element-1660" data-mp="2rjh2q7vse0z4">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary rounded-full blur-[120px] opacity-40 pointer-events-none" data-id="element-1661" data-mp="2iosxghq7ojab" />
        <div className="container max-w-4xl mx-auto text-center relative z-10" data-id="element-1662" data-mp="28r53hu0fjft7">
          <h2 className="text-4xl md:text-5xl font-extrabold font-heading mb-8" data-id="element-1663" data-mp="28p25ont1cidx">
            Confiez-nous votre recrutement
          </h2>
          <p className="text-xl md:text-2xl text-lavande mb-12 max-w-3xl mx-auto leading-relaxed font-medium" data-id="element-1664" data-mp="1zpylthm1nyyd">
            "Vous cherchez à renforcer vos équipes avec un alternant motivé ?
            Remplissez ce formulaire pour nous faire part de votre besoin."
          </p>
          <div className="mt-8 text-primary-dark" data-id="element-1665" data-mp="volsy6lgw0ue">
            <EntrepriseForm data-id="element-1666" data-mp="vpdojhf2dyko" />
          </div>
          <div className="mt-12 pt-8 border-t border-white/10" data-id="element-1667" data-mp="10o884lkuvr5u">
            <Link to="/candidater" className="text-lg text-lavande hover:text-white transition-colors underline underline-offset-4" data-id="element-1668" data-mp="10mnfj1i3qiam">
              Vous cherchez une formation ? Candidater
            </Link>
          </div>
        </div>
      </section>
    </div>;
}